import rwdxNotification, { NotificationType } from './rwdx/components/RWDXNotification';

const impactForm = () => {
	const form = document.querySelector('[data-impact-form]') as HTMLFormElement;
	if (!form) return;

	const categoryCheckboxes = document.getElementById('category-checkboxes');
	if (!categoryCheckboxes) return;

	const radioButtons = categoryCheckboxes.querySelectorAll('input[type="radio"]') as NodeListOf<HTMLInputElement>;

	const setCategory = (category: string) => {
		const categorySubForms = document.querySelectorAll('.secondary-category-checkboxes')

		for (const subForm of categorySubForms) {
			if (subForm.getAttribute('data-category') === category) {
				subForm.removeAttribute('aria-hidden')
			} else {
				const inputs = subForm.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;

				for (const input of inputs) {
					if (input.checked) {
						input.checked = false
					}
				}

				subForm.setAttribute('aria-hidden', 'true')
			}
		}
	}

	const getCategory = (e: Event) => {
		e.preventDefault()
		const target = e.target as HTMLInputElement
		const category = target.value;

		setCategory(category)
	}

	for (const radioButton of radioButtons) {
		radioButton.addEventListener('change', (e) => getCategory(e))
	}

	// Setup character counters for textareas with maxlength
	const textareas = form.querySelectorAll('textarea[maxlength]') as NodeListOf<HTMLTextAreaElement>;

	for (const textarea of textareas) {
		const maxLength = parseInt(textarea.getAttribute('maxlength') || '0', 10);
		if (maxLength <= 0) continue;

		// Create wrapper
		const wrapper = document.createElement('div');
		wrapper.className = 'textarea-wrapper';
		textarea.parentNode?.insertBefore(wrapper, textarea);
		wrapper.appendChild(textarea);

		// Create counter element
		const counter = document.createElement('span');
		counter.className = 'textarea-char-counter';
		counter.textContent = `${textarea.value.length}/${maxLength}`;
		wrapper.appendChild(counter);

		// Update counter on input
		textarea.addEventListener('input', () => {
			counter.textContent = `${textarea.value.length}/${maxLength}`;
		});
	}

	const futureBenefitsSection = document.querySelector('[data-category="future-benefits"]')
	if (futureBenefitsSection) {
		const futureBenefitsRadios = futureBenefitsSection.querySelectorAll('input[type="radio"]') as NodeListOf<HTMLInputElement>;
		const ifYesContainer = document.querySelector('[data-if-yes-field]') as HTMLElement;

		if (ifYesContainer) {
			for (const radio of futureBenefitsRadios) {
				radio.addEventListener('change', (e) => {
					const target = e.target as HTMLInputElement
					const value = target.value

					if (value === 'yes') {
						ifYesContainer.removeAttribute('aria-hidden')
					} else {
						ifYesContainer.setAttribute('aria-hidden', 'true')
					}
				})
			}
		}
	}

	// Handle submitter type conditional fields
	const submitterTypeSection = document.querySelector('[data-submitter-type-section]')
	if (submitterTypeSection) {
		const submitterTypeRadios = submitterTypeSection.querySelectorAll('input[type="radio"][name="submitterType"]') as NodeListOf<HTMLInputElement>;
		const individualField = document.querySelector('[data-submitter-type-field="individual"]') as HTMLElement;
		const teamField = document.querySelector('[data-submitter-type-field="team"]') as HTMLElement;

		if (individualField && teamField) {
			for (const radio of submitterTypeRadios) {
				radio.addEventListener('change', (e) => {
					const target = e.target as HTMLInputElement
					const value = target.value

					if (value === 'individual') {
						individualField.removeAttribute('aria-hidden')
						teamField.setAttribute('aria-hidden', 'true')
						// Clear team field when switching to individual
						const teamTextarea = teamField.querySelector('textarea') as HTMLTextAreaElement;
						if (teamTextarea) teamTextarea.value = '';
					} else if (value === 'team') {
						teamField.removeAttribute('aria-hidden')
						individualField.setAttribute('aria-hidden', 'true')
						// Clear individual field when switching to team
						const individualInput = individualField.querySelector('input') as HTMLInputElement;
						if (individualInput) individualInput.value = '';
					}
				})
			}
		}
	}

	// Handle supervisor approval conditional field
	const supervisorApprovalSection = document.querySelector('[data-supervisor-approval-section]')
	if (supervisorApprovalSection) {
		const supervisorCheckbox = supervisorApprovalSection.querySelector('input[type="checkbox"][name="supervisorApproval"]') as HTMLInputElement;
		const supervisorEmailField = document.querySelector('[data-supervisor-email-field]') as HTMLElement;

		if (supervisorCheckbox && supervisorEmailField) {
			supervisorCheckbox.addEventListener('change', () => {
				if (supervisorCheckbox.checked) {
					supervisorEmailField.removeAttribute('aria-hidden')
				} else {
					supervisorEmailField.setAttribute('aria-hidden', 'true')
					// Clear email field when unchecking
					const emailInput = supervisorEmailField.querySelector('input') as HTMLInputElement;
					if (emailInput) emailInput.value = '';
				}
			})
		}
	}

	// Form submission handler
	let isSubmitting = false;

	const allowedEmailDomains = [
		'@animex.pl',
		'@agriplus.pl',
		'@mecom.sk',
		'@monells.es',
		'@argal.com',
		'@salaisonogier.com',
		'@justgourmetgroup.com',
		'@comtim.ro',
		'@morlinyfoods.co.uk',
		'@goodies.com.ro',
		'@elitcugir.ro'
	];

	const validateEmail = (): string | null => {
		const emailInput = document.getElementById('email') as HTMLInputElement;
		if (!emailInput || !emailInput.value) return null;

		const email = emailInput.value.toLowerCase().trim();
		const hasAllowedDomain = allowedEmailDomains.some(domain => email.endsWith(domain.toLowerCase()));

		if (!hasAllowedDomain) {
			return `Email must be from an authorized company domain (${allowedEmailDomains.join(', ')})`;
		}

		return null;
	};

	const validateConditionalFields = (): string | null => {
		// Check if supervisor email is required and filled when checkbox is checked
		const supervisorApprovalCheckbox = form.querySelector('input[type="checkbox"][name="supervisorApproval"]') as HTMLInputElement;
		if (supervisorApprovalCheckbox?.checked) {
			const supervisorEmailInput = document.getElementById('supervisorEmail') as HTMLInputElement;
			if (!supervisorEmailInput?.value?.trim()) {
				return 'Please enter an email address for your supervisor';
			}
		}

		// Check if "other areas description" is required and filled when future benefits is "yes"
		const futureBenefitsYes = form.querySelector('input[name="futureBenefitsDevelopment"][value="yes"]') as HTMLInputElement;
		if (futureBenefitsYes?.checked) {
			const otherAreasInput = document.getElementById('otherAreasDescription') as HTMLTextAreaElement;
			if (!otherAreasInput?.value?.trim()) {
				return 'Please enter the areas involved in the development of future benefits';
			}
		}

		// Check if team members is required and filled when submitter type is "team"
		const submitterTypeTeam = form.querySelector('input[name="submitterType"][value="team"]') as HTMLInputElement;
		if (submitterTypeTeam?.checked) {
			const teamMembersInput = document.getElementById('teamMembers') as HTMLTextAreaElement;
			if (!teamMembersInput?.value?.trim()) {
				return 'Please enter the names of your team members';
			}
		}

		return null;
	};

	const validateFiles = (): string | null => {
		const projectDocsInput = document.getElementById('projectDocuments') as HTMLInputElement;
		const workPlanInput = document.getElementById('workPlanFile') as HTMLInputElement;

		// Validate project documents
		if (projectDocsInput && projectDocsInput.files && projectDocsInput.files.length > 0) {
			const files = Array.from(projectDocsInput.files);

			console.log(`Validating ${files.length} project document file(s)`);

			if (files.length > 3) {
				return `Maximum 3 project document files allowed. You selected ${files.length} files.`;
			}

			let totalSize = 0;
			const invalidFiles: string[] = [];

			for (const file of files) {
				console.log(`File: ${file.name}, Type: ${file.type}, Size: ${file.size}`);
				if (file.type !== 'application/pdf') {
					invalidFiles.push(file.name);
				}
				totalSize += file.size;
			}

			if (invalidFiles.length > 0) {
				return `The following file(s) must be PDF: ${invalidFiles.join(', ')}`;
			}

			if (totalSize > 16 * 1024 * 1024) { // 16MB
				const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
				return `Total size of project documents (${totalSizeMB}MB) exceeds 16MB limit`;
			}
		}

		// Validate work plan
		if (workPlanInput && workPlanInput.files && workPlanInput.files.length > 0) {
			const file = workPlanInput.files[0];

			console.log(`Work plan file: ${file.name}, Type: ${file.type}, Size: ${file.size}`);

			if (file.type !== 'application/pdf') {
				return 'Work plan must be a PDF file';
			}

			if (file.size > 4 * 1024 * 1024) { // 4MB
				const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
				return `Work plan file size (${fileSizeMB}MB) exceeds 4MB limit`;
			}
		}

		return null;
	};

	const showError = (message: string) => {
		rwdxNotification.notify(message, NotificationType.ERROR, 5000);
	};

	const showSuccess = () => {
		rwdxNotification.notify('Thank you! Your submission has been received successfully.', NotificationType.INFO, 5000);

		// Reset form after a delay
		setTimeout(() => {
			form.reset();
			// Reset category visibility
			document.querySelectorAll('.secondary-category-checkboxes').forEach(subForm => {
				subForm.setAttribute('aria-hidden', 'true');
			});
			const ifYesContainer = document.querySelector('[data-if-yes-field]') as HTMLElement;
			if (ifYesContainer) {
				ifYesContainer.setAttribute('aria-hidden', 'true');
			}
			// Reset submitter type fields
			document.querySelectorAll('[data-submitter-type-field]').forEach(field => {
				field.setAttribute('aria-hidden', 'true');
			});
			// Reset supervisor email field
			const supervisorEmailField = document.querySelector('[data-supervisor-email-field]') as HTMLElement;
			if (supervisorEmailField) {
				supervisorEmailField.setAttribute('aria-hidden', 'true');
			}
		}, 3000);
	};

	const handleSubmit = async (e: SubmitEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (isSubmitting) return;

		// Validate email domain
		const emailError = validateEmail();
		if (emailError) {
			showError(emailError);
			return;
		}

		// Validate files
		const fileError = validateFiles();
		if (fileError) {
			showError(fileError);
			return;
		}

		// Check HTML5 validation
		if (!form.checkValidity()) {
			form.reportValidity();
			return;
		}

		// Validate conditional required fields
		const conditionalError = validateConditionalFields();
		if (conditionalError) {
			showError(conditionalError);
			return;
		}

		isSubmitting = true;

		// Show loading state
		const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
		if (!submitButton) return;
		const originalButtonText = submitButton.textContent;
		submitButton.disabled = true;
		submitButton.textContent = 'Submitting...';

		// Collect form data - FormData will automatically collect all fields with name attributes
		const formData = new FormData(form);

		try {
			const response = await fetch('/impact.php', {
				method: 'POST',
				body: formData
			});

			let result;
			try {
				result = await response.json();
			} catch {
				result = { message: await response.text() };
			}

			if (!response.ok) {
				if (result.errors && Array.isArray(result.errors)) {
					showError(result.errors.join(', '));
				} else {
					showError(result.message || 'There was an error submitting your form. Please try again.');
				}
			} else {
				showSuccess();
			}
		} catch (error) {
			showError('Network error. Please check your connection and try again.');
		} finally {
			isSubmitting = false;
			submitButton.disabled = false;
			submitButton.textContent = originalButtonText;
		}
	};

	form.addEventListener('submit', handleSubmit);
}

export default impactForm
